import { EncodingHelper } from '@rightship-tech/common-ui/helpers/encoding.helper';
import { CookieHelper } from '@rightship-tech/common-ui/helpers/cookie-helper';
import { EntityAttachmentManager } from '@rightship-tech/common-ui/services/attachments/entity-attachment-manager';
import { AttachmentDialogModel, EntityAttachmentModel } from '@rightship-tech/common-ui/models/attachments.models';
import { EntityTypes, SubtaskSourceEnum } from '@rightship-tech/common-ui/models/basic.models';
import { autoinject, bindable, computedFrom, transient } from "aurelia-framework";
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { Router } from 'aurelia-router';
import { DialogClientService } from '@rightship-tech/common-ui/services/basic/dialog-client.service';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import * as moment from 'moment';
import * as Enumerable from "linq";
import { CorrespondenceClientModel, CorrespondenceEmailModel, GetCorrespondenceResponse, type MessageCenterTaskModel, RightshipRecipientConfigModel, SubtaskStatusEnum, VesselCorrespondence } from '../../../models/message-centre.models';
import { MessageCentreService } from '../../../services/message-centre.service';
import { MessageCentreDialogConstants } from '../../../constants/dialog.constants';
import { EmailRecipientTypeEnum, QuickStartPageEnum, QuickstartTabModel } from '../../../models/common.models';
import { IntroduceMessageCenter } from './quickstart/introduce-message-center/introduce-message-center';
import { SendReceiveRmail } from './quickstart/send-receive-email/send-receive-email';
import { PageActionsAvailable } from './quickstart/actions-available/actions-available';
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from '../../../constants/signal.constant';
import { FileHelper } from '@rightship-tech/common-ui/helpers/file-helper';
import { UserModel } from '@rightship-tech/common-ui/models/user.models';
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import DOMPurify from 'dompurify';
import { CloneHelper } from '@rightship-tech/common-ui/helpers/clone.helper';

@autoinject
@transient()
export class MessageCentre {

    public unlinkedUrlRegex = /(?<!<\/?a[^>]*>)(?<!href=["'])(http[s]?:\/\/[^\s<>"']+)(?![^<]*<\/a>)/g;

    public search: string = "";
    public label: string = "";
    public loadingThreads: boolean = true;
    public loadingMessages: boolean;
    public isLoadingSetAwaitingResponse: boolean;
    public isLoadingMarkThreadAsCompleted: boolean;
    public threads: VesselCorrespondence[] = [];
    public filteredThreads: VesselCorrespondence[] = [];
    public messages: CorrespondenceClientModel[] = [];
    public message: string = "";
    public selectedThread: GetCorrespondenceResponse;
    public selectedThreadMobile: boolean;
    public messagesScrollElem: HTMLElement;
    public addedPeople: CorrespondenceEmailModel[] = [];
    public addedPeopleVisible: boolean = false;
    public attachmentCorrespondenceId: number;
    public attachmentsModelToBeAdded: AttachmentDialogModel[] = [];
    public selectedThreadName: string;
    public selectedThreadSubject: string;
    public collections: QuickstartTabModel[];
    public shouldShowQuickstart: boolean = false;
    public expandedInput: boolean = false;
    public onSubtaskCorrespondenceReplyFunc: (x: CorrespondenceClientModel) => void;
    public openedSendMessageDialog: boolean = false;

    private _scrollTimeout: ReturnType<typeof setTimeout>;
    private subscribedToThreadGuid: string;

    @bindable
    public task: MessageCenterTaskModel;

    @bindable
    public canAddToDocuments: boolean = false;

    @bindable
    public quickStartPages: number[];

    @bindable
    public isRightshipUser: boolean = false;

    @bindable
    public showSetAsAwaitingResponseButton: boolean = false;

    @bindable
    public showMarkThreadAsCompletedButton: boolean = false;

    @bindable
    public allowInitNewThread: boolean = true;
    
    @bindable
    public rightshipRecipientConfig: RightshipRecipientConfigModel = {
        //If true, Rightship will be added to the recipient list by default
        addedRsAsRecipient: true,
        //Option to add/remove RightShip from the recipient list
        canEditRightshipRecipient: null, //Leave as null to ignore tied logic
        onRsRecipientToggle: () => {}
    }

    get isRightShipInspection() {
        return this.task && this.task.entityType == EntityTypes.RightShipInspection;
    }

    get canMarkConversationAsAllReadOrCompleted() {
        //for RightShipInspection only if the user has the permission to mark the conversation as all read or completed
        return this.userClaimsService.hasPermission(Claims.CanMarkConversationAsAllReadOrCompleted);        
    }

    @bindable
    public quickStartThread: { subject: string, latestConversaionSummary: string, latestSenderEmail: string };

    @bindable
    public canUpdateToPendingReview: boolean = false;

    @bindable
    public showSendMessageDialog: boolean = false;

    @computedFrom("selectedThread.entityTypeId")
    get selectedThreadType() {
        if(this.selectedThread?.entityTypeId && (this.selectedThread.entityTypeId == EntityTypes.PSC || this.selectedThread.entityTypeId == EntityTypes.Incident)) {
            return EntityTypes[this.selectedThread.entityTypeId];
        }
    }

    @computedFrom("task", "task.entityType")
    get isCrewWelfare() {
        if(this.task.entityType == EntityTypes.CrewWelfare) {
            return true;
        }
    }

    @computedFrom("message")
    get messageText() {
        if (this.message && !$(this.message).get(0).innerText.trim()) {
            return "";
        }
        return this.message;
    }

    @computedFrom("selectedThread", "selectedThread.subtaskStatusId", "selectedThread.isRightShipAlerted", "showSetAsAwaitingResponseButton")
    get showAwaitingResponseButton(): boolean {
        return this.showSetAsAwaitingResponseButton 
            && this.selectedThread?.subtaskId != null 
            && (this.selectedThread?.subtaskStatusId == null 
                || this.selectedThread.subtaskStatusId === SubtaskStatusEnum.Pending
                || this.selectedThread.subtaskStatusId === SubtaskStatusEnum.PendingReview
                || this.selectedThread.subtaskStatusId === SubtaskStatusEnum.Replied
            )
            && !this.selectedThread.isRightShipAlerted; 
    }

    @computedFrom("selectedThread", "selectedThread.subtaskStatusId", "showMarkThreadAsCompletedButton", "loadingMessages")
    get showCompletedButton(): boolean {
        if(this.loadingMessages) return false;
        return (this.showMarkThreadAsCompletedButton
            && (this.selectedThread?.subtaskStatusId != null || this.selectedThread.isRightShipAlerted)
            && (this.selectedThread.subtaskStatusId === SubtaskStatusEnum.PendingReview
                || this.selectedThread.subtaskStatusId === SubtaskStatusEnum.Replied
                || (this.selectedThread.subtaskStatusId === SubtaskStatusEnum.Pending && this.selectedThread?.correspondence?.filter(x => x && !this.isMyMessage(x))?.length > 0)
                || ((!this.selectedThread.subtaskStatusId || this.selectedThread.subtaskStatusId === SubtaskStatusEnum.Pending) && this.selectedThread.isRightShipAlerted)
            )
            || (this.canMarkConversationAsAllReadOrCompleted && this.isRightShipInspection && this.selectedThread.subtaskStatusId !== SubtaskStatusEnum.Closed) 
        );
    }

    @computedFrom('threads')
    public get getAllUnreadMessagesCount() {
        let count = 0;
        this.threads.forEach(thread => {
            count += thread.unreadMessageCount;
        });
        return count;
    }

    get currentUser(): UserModel{
        return this.userClientService.currentUser;
    }

    constructor(private router: Router, private messageCentreService: MessageCentreService, private dialogClientService: DialogClientService, 
        private notificationClientService: NotificationClientService, public entityAttachmentManager: EntityAttachmentManager,
        private introduceMessageCenter: IntroduceMessageCenter, private sendReceiveEmail: SendReceiveRmail, private actionsAvailable: PageActionsAvailable,
        private configService: ConfigService, private signalClientService: SignalClientService, private userClientService:UserClientService, private userClaimsService: UserClaimsService,
        private element: Element) {
            this.onSubtaskCorrespondenceReplyFunc = this.onCorrespondenceReplyEvent.bind(this);
        }

    encryptedGuid:string;
    encryptedSubCorrespondenceId:string;

    activate() {
        //Check cookie
        const key = `queryString-${this.configService.config.environment}`;
		const queryString = CookieHelper.readCookie(key);
        
		if(queryString && queryString != "") {

			const urlParams = new URLSearchParams(queryString);
			if(urlParams.get("entityType") && urlParams.get("entityType") != "") {
                this.task.entityType = parseInt(EncodingHelper.decodeUrl(urlParams.get("entityType"))) as EntityTypes;
            }
			if(urlParams.get("taskId") && urlParams.get("taskId") != "") {
                this.task.taskId = parseInt(EncodingHelper.decodeUrl(urlParams.get("taskId")));
            }
			if(urlParams.get("vesselImo") && urlParams.get("vesselImo") != "") {
                this.task.vesselImo = EncodingHelper.decodeUrl(urlParams.get("vesselImo"));
            }
			if(urlParams.get("vesselName") && urlParams.get("vesselName") != "") {
                this.task.vesselName = EncodingHelper.decodeUrl(urlParams.get("vesselName"));
            }
            if (urlParams.get("eg") && urlParams.get("eg") != "") {
                this.encryptedGuid = EncodingHelper.decodeUrl(urlParams.get("eg"));
            }
            if (urlParams.get("esc") && urlParams.get("esc") != "") {
                this.encryptedSubCorrespondenceId = EncodingHelper.decodeUrl(urlParams.get("esc"));
            }


            //Delete cookie
            CookieHelper.eraseCookie(key, this.configService.config.environment);
        }
    }

    async attached() {        

        if (this.userClientService.currentUser?.user && this.encryptedGuid && this.encryptedSubCorrespondenceId) {
            this.task.subtaskId = await this.messageCentreService.getSubtaskId(this.encryptedGuid, this.encryptedSubCorrespondenceId);
        }
        await this.refreshThreads();
        this.signalClientService.subscribe(SignalFunctions.correspondenceMessageRead, (data) => {
            this.refreshMessageReadIndicator(data);
        });

        this.signalClientService.subscribe(SignalFunctions.correspondenceMessageReadAll, (data) => {
            if (this.isRightshipUser && this.selectedThread && data.subtaskId == this.selectedThread.subtaskId) {
                const selectedThread = this.filteredThreads.find(x => x.subtaskId == data.subtaskId);
                if (selectedThread) this.selectThread(selectedThread, true);
            }
        });

        if (this.showSendMessageDialog) {
            this.openSendMessageDialog();
        }
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.correspondenceMessageRead, (data) => {
            this.refreshMessageReadIndicator(data);
        });

        this.signalClientService.unsubscribe(SignalFunctions.correspondenceMessageReadAll, () => {
            if (this.isRightshipUser) {
                const selectedThread = this.filteredThreads.find(x => x.subtaskId == this.selectedThread.subtaskId);
                this.selectThread(selectedThread, true);
            }
        });
        if (this.subscribedToThreadGuid) {
            this.signalClientService.unsubscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.subscribedToThreadGuid}`, this.onSubtaskCorrespondenceReplyFunc);
        }
    }

    onCorrespondenceReplyEvent(x: CorrespondenceClientModel) {
        const currentUser = this.userClientService.currentUser?.user;
        if (currentUser && currentUser.email != x.user && currentUser.fullName != x.user) {
            this.refreshThreads(true);
        }
    }

    refreshMessageReadIndicator(data: any) {
        if (data.correspondenceStatuses && data.readByEmail == this.task.emailIdentifier) {
            data.correspondenceStatuses.forEach(status => {
                const message = this.messages.find(x => x.subtaskCorrespondenceId == status.subtaskCorrespondenceId);
                if (message) {
                    message.isRead = status.isRead;
                }
            });
            const currentThread = this.filteredThreads.find(x => x.subtaskId == data.subtaskId);
            if (currentThread) {
                if (this.messages.every(x => x.isRead)) {
                    currentThread.unreadMessageCount = 0;
                } else {
                    currentThread.unreadMessageCount = this.messages.filter(x => !x.isRead).length;
                }
            }
        }
    }

    async refreshThreads(forceReload: boolean = false) {
        this.threads = await this.messageCentreService.getCorrespondences(this.task.taskId, this.task.entityType, null, null, null, this.task.encryptedParams);
        this.filterThreads();

        if(this.filteredThreads.length > 0) {
            const thread = this.filteredThreads.find(x => x.subtaskId == this.selectedThread?.subtaskId || x.subtaskId == this.task?.subtaskId);
            if (thread)
                this.selectThread(thread, forceReload);
            else
                this.selectThread(this.filteredThreads[0], forceReload);
        }
        this.loadingThreads = false;
    }

    get shouldDisplayMarkAsReadForEveryone() {
        if (!this.isRightshipUser || (this.isRightShipInspection && !this.canMarkConversationAsAllReadOrCompleted)) return false;
        if (this.message) {
            const latestMessage = this.messages.reduce((prev, current) => {
                return (prev && prev.subtaskCorrespondenceId > current.subtaskCorrespondenceId) ? prev : current
            });
            return latestMessage.readModel == null;
        }
    }

    shouldDisplayReadAllIndicator(message: CorrespondenceClientModel) {
        return message.readModel && this.isRightshipUser;
    }

    async selectThread(thread: VesselCorrespondence, force: boolean = false) {
        this.selectedThreadMobile = true;

        //Quick start guide carousel pages
        if (thread.subtaskId == -1) {
            this.shouldShowQuickstart = true;
            this.collections = [
                {
                    vm: this.introduceMessageCenter,
                    path: "@rightship-tech/message-centre-app/views/pages/message-centre/quickstart/introduce-message-center/introduce-message-center",
                    show: this.quickStartPages.includes(QuickStartPageEnum.IntroduceMessageCenter),
                    model: {
                        email: this.task.emailIdentifier
                    }
                },
                {
                    vm: this.sendReceiveEmail,
                    path: "@rightship-tech/message-centre-app/views/pages/message-centre/quickstart/send-receive-email/send-receive-email",
                    show: this.quickStartPages.includes(QuickStartPageEnum.SendReceiveEmail),
                    model: {
                    }
                },
                {
                    vm: this.actionsAvailable,
                    path: "@rightship-tech/message-centre-app/views/pages/message-centre/quickstart/actions-available/actions-available",
                    show: this.quickStartPages.includes(QuickStartPageEnum.ActionAvailable),
                    model: {
                        isShipManager: this.task.isShipManager,
                        isFreelancer: this.task.isFreelancer,
                        assignmentStatusActive: this.task.assignmentStatusActive,
                        isInspectionCompany: this.task.isInspectionCompany,
                        encryptedParams: this.task.encryptedParams,
                        allowInitNewThread: this.allowInitNewThread,
                        reviseProposal: () => this.task.reviseProposal(),
                        withdrawInspection: () => this.task.withdrawInspection(),
                        changeInspectionDate: () => this.task.changeInspectionDate(),
                        cancelInspection: () => this.task.cancelInspection(),
                        startNewConversation: () => this.openSendMessageDialog()
                    }
                }
            ];
            
            const correspondenceResponse: GetCorrespondenceResponse = { subtaskId: -1, subtaskStatusId: null, guid: null, 
                modifiedBy: null, modifiedOn: null, vesselName: null, isInvitation: null, description: null, vesselImo: null, requestorName: null, correspondence: null, subtaskStatusHistory: null, entityTypeId: null, isCloseout: false };
            this.selectedThread = correspondenceResponse;
            return;
        } else {
            this.shouldShowQuickstart = false;
        }

        if (this.selectedThread && this.selectedThread.subtaskId == thread.subtaskId && !force) {
            return;
        }

        this.loadingMessages = true;

        // Clear the previous timeout
        clearTimeout(this._scrollTimeout);

        //Get correspondence
        const correspondenceResponse: GetCorrespondenceResponse = { subtaskId: thread.subtaskId, subtaskStatusId: null, guid: null, 
            modifiedBy: null, modifiedOn: null, vesselName: null, isInvitation: null, description: null, vesselImo: null, requestorName: null, correspondence: null, subtaskStatusHistory: null, entityTypeId: null, isCloseout: false };
        this.selectedThread = correspondenceResponse;
        this.selectedThreadSubject = thread.subject;

        this.selectedThread = await this.messageCentreService.getCorrespondence(thread.subtaskGuid, this.task.encryptedParams);
        
        //Go through the correspondence of the current SelectedThread and set the awaiting response and marked thread as completed indicators
        if (this.showSetAsAwaitingResponseButton || this.showMarkThreadAsCompletedButton || this.isRightShipInspection) {
            const subtaskStatusHistory = this.selectedThread.subtaskStatusHistory;
            this.selectedThread.correspondence.forEach(x => {

                //Go through the subtask status history
                subtaskStatusHistory.forEach(history => {

                    //Set the awaiting response indicator
                    if (this.showSetAsAwaitingResponseButton && history.createdDate > x.createdDate && 
                        history.subtaskStatusSourceId == SubtaskSourceEnum.MessageCentre && history.statusId == SubtaskStatusEnum.AwaitingResponse) {

                        x.setAsAwaitingResponse = true;
                        x.statusUser = history.user;
                        x.statusDate = history.createdDate;
                        subtaskStatusHistory.splice(subtaskStatusHistory.indexOf(history), 1);
                        return;
                    }

                    //Mark thread as completed
                    if ((this.showMarkThreadAsCompletedButton  || this.isRightShipInspection) 
                        && history.createdDate > x.createdDate && history.subtaskStatusSourceId == SubtaskSourceEnum.MessageCentre && history.statusId == SubtaskStatusEnum.Closed) {

                        x.markedThreadAsCompleted = true;
                        x.statusUser = history.user;
                        x.statusDate = history.createdDate;
                        subtaskStatusHistory.splice(subtaskStatusHistory.indexOf(history), 1);
                        return;
                    }
                });
            });
        }

        //Label
        this.label = this.selectedThread.subject ? this.selectedThread.subject : this.removeHtmlTags(`${this.selectedThread.vesselName} - ${this.selectedThread.vesselImo} -  ${this.selectedThread.description}`);

        // If rightship user, we apply the rs internal read indicator logic by marking all the message before the latest read message by RS user as read
        if (this.isRightshipUser) {
            const latestReadByRSUser = this.selectedThread.correspondence.findIndex(x => x.readModel != null);
            if (latestReadByRSUser != -1) {
                this.selectedThread.correspondence.forEach((message, index) => {
                    if (index >= latestReadByRSUser) {
                        message.markedAsReadByRSUser = true;
                    }
                });
            }
        }

        this.messages = this.selectedThread.correspondence.reverse();
        this.selectedThreadName = thread.name;

        // In the first load, check if all message not belongs to self is read or not, if yes, mark the thread as read
        this.overrideThreadReadIndicator();

        let lastMsg = Enumerable.from(this.selectedThread.correspondence)
            .orderByDescending(x => x.createdDate)
            .where(x => x.messageSenderEmail != null)
            .firstOrDefault();

        if (lastMsg == null) {
            lastMsg = Enumerable.from(this.selectedThread.correspondence)
            .orderByDescending(x => x.createdDate)
            .firstOrDefault();
        }

        // Use CloneHelper.clone to deep clone the emails array
        let emails = CloneHelper.clone(lastMsg?.emails ?? []);

        emails.push({
            email: lastMsg?.messageSenderEmail,
            emailRecipientTypeId: EmailRecipientTypeEnum.To,
            recipientTypeName: lastMsg?.messageSenderEmailTypeName
        });

        emails = emails.filter(x => x.email != null && x.email != this.task.emailIdentifier);
        
        this.addedPeople = emails ? emails.flatMap(x => {
            // Exclude self from the addedPeople list
            const currentEmailSameAsEmailIdentifier = x.email == this.task.emailIdentifier && (x.recipientTypeName == this.task.emailIdentifierTypeName || this.task.emailIdentifierTypeName == null);
            if (!currentEmailSameAsEmailIdentifier) {
                return {
                    email: x.email,
                    emailRecipientTypeId: x.emailRecipientTypeId,
                    recipientTypeName: x.recipientTypeName
                }
            }
        }).filter(x => x): [];

        // Once the receiver click on the reply link, if the sender not included within the correspondence email, should automatically add him to addedPeople list
        const lastMessage = this.messages.at(-1);
        const hasIncludedInAddedPeople = this.addedPeople.some(x => x.email == lastMessage.messageSenderEmail && x.recipientTypeName == lastMessage.messageSenderEmailTypeName);
        if (lastMessage && lastMessage.messageSenderEmail && lastMessage.messageSenderEmail != this.task.emailIdentifier && !hasIncludedInAddedPeople) {
            this.addedPeople.push({
                email: lastMessage.messageSenderEmail,
                recipientTypeName: lastMessage.messageSenderEmailTypeName,
                emailRecipientTypeId: EmailRecipientTypeEnum.To
            });
        }

        if (this.subscribedToThreadGuid != this.selectedThread.guid) {
            if (this.subscribedToThreadGuid) {
                this.signalClientService.unsubscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.subscribedToThreadGuid}`, this.onSubtaskCorrespondenceReplyFunc);
            }
            this.subscribedToThreadGuid = this.selectedThread.guid;
            this.signalClientService.subscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.subscribedToThreadGuid}`, this.onSubtaskCorrespondenceReplyFunc);
        }

        this.addedPeopleVisible = true;
        this.loadingMessages = false;

        //Scroll to the bottom of the messages. Set a timeout to allow the DOM to update
        setTimeout(async () => {
            this.scrollToFirstUnread()

            //Call read indicator when thread is not scrolled
            if(this.messagesScrollElem) {
                await this.markVisibleMessagesAsRead(this.messagesScrollElem);
            }
            await this.markAllAsRead();
        });
    }

    //Scroll to the first unread message
    scrollToFirstUnread() {
        if (this.messagesScrollElem) {
            const lastIndexMarkedReadFromRS = this.findLastIndex(this.messages, message => message.markedAsReadByRSUser);
            //ET2-2065: RS user will always scroll to the bottom where the latest message appear
            let firstUnread = null;
            if (!this.isRightshipUser) {
                firstUnread = this.messages.find(message => !message.isRead, lastIndexMarkedReadFromRS);
            }
            if (firstUnread) {
                const index = this.messages.indexOf(firstUnread);
                // If the last message is unread, scroll to the bottom
                if (index === this.messages.length - 1) {
                    this.messagesScrollElem.scrollTop = this.messagesScrollElem.scrollHeight;
                    return;
                }
                const element = document.querySelector(`.messages-scroller .message:nth-child(${index + 1})`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            } else {
                document.querySelector('.messages-scroller .message .read-status')?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }

    async markAsRead(messages: CorrespondenceClientModel[]) {
        const markAsReadMessages = messages?.filter(m => !m.ignoreFromReadScrolling);
        if (markAsReadMessages && markAsReadMessages.length != 0) {
            await this.messageCentreService.updateCorrespondenceReadIndicator({ 
                subtaskId: this.selectedThread.subtaskId,
                email: this.task.emailIdentifier,
                correspondenceReadStatuses: markAsReadMessages.map(m => { return { subtaskCorrespondenceId: m.subtaskCorrespondenceId, isRead: true } })
            });
            messages.forEach(m => m.isRead = true);
            this.overrideThreadReadIndicator();
        }
    }

    async markAllAsRead() {
        const readMessages = this.messages.filter(message => !message.isRead);
        await this.markAsRead(readMessages);
    }

    async markAsUnRead(message: CorrespondenceClientModel) {
        await this.messageCentreService.updateCorrespondenceReadIndicator({ 
            subtaskId: this.selectedThread.subtaskId,
            email: this.task.emailIdentifier,
            correspondenceReadStatuses: [{ subtaskCorrespondenceId: message.subtaskCorrespondenceId, isRead: false }]
        });
        message.isRead = false;
        message.ignoreFromReadScrolling = true;
        this.overrideThreadReadIndicator(true);
    }

    handleScroll(event: Event) {
        const element = event.target as HTMLElement;

        // Clear the previous timeout
        clearTimeout(this._scrollTimeout);

        // Set a new timeout
        this._scrollTimeout = setTimeout(async () => {
            await this.markVisibleMessagesAsRead(element);
            if (element.scrollHeight - element.scrollTop === element.clientHeight) {
                this.markAllAsRead();
            }
        }, 3000);
    }

    async markVisibleMessagesAsRead(element: HTMLElement) {
        if (element) {
            const messageElements = element.querySelectorAll('.message');
            let currentVisibleIndex = -1;
            const readMessages: CorrespondenceClientModel[] = [];
    
            // Find the index of the first fully visible message
            messageElements.forEach((messageElement, index) => {
                const rect = messageElement.getBoundingClientRect();
                if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
                    currentVisibleIndex = index;
                    return;
                }
            });
        
            // Mark all previous messages as read if they are not already read
            if (currentVisibleIndex !== -1) {
                for (let i = 0; i <= currentVisibleIndex; i++) {
                    if (!this.messages[i].isRead) {
                        readMessages.push(this.messages[i]);
                    }
                }
                if (readMessages.length > 0) await this.markAsRead(readMessages);
            }
        }
    }

    //Mark as read for everyone
    public async markAsReadForEveryone() {
        const latestMessage = this.messages[this.messages.length - 1];
        const continueExecution = this.dispatchEvent('mark-as-read', { subtaskCorrespondenceId: latestMessage?.subtaskCorrespondenceId, subtaskId: this.selectedThread?.subtaskId});
        if(continueExecution)
        {
            await this.messageCentreService.markAllAsRead(this.selectedThread.subtaskId, { subtaskCorrespondenceId: latestMessage.subtaskCorrespondenceId });

            const thread = this.threads.filter(x => x.subtaskId == this.selectedThread.subtaskId)[0];
            await this.selectThread(thread, true);
        }
    }

    //Send custom event for set awaiting response - ET1-1889
    public sendCustomEventForSetAwaitingResponse() {
        this.dispatchEvent('status-changing', { statusId: SubtaskStatusEnum.AwaitingResponse, subtaskId: this.selectedThread?.subtaskId});
    }

    //Set Due Diligence as awaiting response - Being called via view-model.ref within due-diligence-message-centre.ts
    public async setAsAwaitingResponse() {
        try {
            this.isLoadingSetAwaitingResponse = true;
            await this.messageCentreService.setAsAwaitingReponse(this.selectedThread.subtaskId);
            await this.refreshThreads(true);
        }
        catch (error) {
            this.notificationClientService.show("Error", "An error occurred while setting the thread as awaiting response.", NotificationType.Error);
            console.error(error);
        }
        finally {
            this.isLoadingSetAwaitingResponse = false;
        }
    }
    
    //Send custom event for mark thread as completed - ET1-1910
    public sendCustomEventForMarkThreadAsCompleted() {
       
        if (this.isRightShipInspection) {
            this.dialogClientService.show(MessageCentreDialogConstants.MarkAsCompleteDialog, {
                onProceed: async () => {
                    await this.markThreadAsCompleted()
                }
            }, true, false, false);
        } else {
            this.dispatchEvent('status-changing', { statusId: SubtaskStatusEnum.Closed });
        }
    }

    //Mark tread as completed
    public async markThreadAsCompleted() {
        try {
            this.isLoadingMarkThreadAsCompleted = true;
            await this.messageCentreService.setAsClosed(this.selectedThread.subtaskId);
            await this.refreshThreads(true);
        }
        catch (error) {
            this.notificationClientService.show("Error", "An error occurred while marking the thread as completed.", NotificationType.Error);
            console.error(error);
        }
        finally {
            this.isLoadingMarkThreadAsCompleted = false;
        }
    }

    private dispatchEvent(nameEvent: string, data?: any): boolean {
        if (this.element && nameEvent) {
            const customEvent: CustomEvent = new CustomEvent(nameEvent, { bubbles: true, cancelable: true, detail: data });
            return this.element.dispatchEvent(customEvent);
        }
        else return true;
    }
    
    //Get attachment icon
    getAttachmentIcon(path: string) {
        const fileExtension = FileHelper.getFileExtension(path);
        if(fileExtension == "pdf"){
            return 'doc-pdf';
        } 
        else if(fileExtension == "excel"){
            return 'excel-file';
        }
        else if(fileExtension == "jpg"){
            return 'doc-jpg';
        }
        else {
            return 'doc';
        }
    }

    //Get file extension
    getFileExtension(path: string) {
        return FileHelper.getFileExtension(path);
    }

    //Get file size
    getFileSize(size: number) {
        return size >= 1000000 ? `${(size/1000000.0).toFixed(2)}MB` : `${(size/1000.0).toFixed(2)}KB`;
    }

    public goBackToThreadsListOnMobile() {
        this.selectedThreadMobile = false;
        this.selectedThread = null;
    }

    public filterThreads() {
        let threads = this.threads.filter(x => !x.isInvitation);
        if (this.search)
            threads = threads.filter(x => x.name.toLowerCase().includes(this.search.toLowerCase()));

        this.filteredThreads = threads;

        //If quictStartThread is available, add it to the list
        if (this.quickStartThread) {
            threads.push(...[
                {
                    subtaskId: -1,
                    subject: this.quickStartThread.subject,
                    latestConversaionSummary: this.quickStartThread.latestConversaionSummary,
                    latestSenderEmail: this.quickStartThread.latestSenderEmail,
                    date: new Date(),
                } as any
            ]);
        }
    }
    
    public getViewModel(name: string) {
        if (name) {
            require( name +'.ts')
            require( name +'.html')
            return name
        }
    }

    public removeHtmlTags(str: string): string {
        const noTags = DOMPurify.sanitize(str, {ALLOWED_TAGS: []});
        const noDoubleSpaces = noTags.replace(/\s{2,}/g, ' ');
        return noDoubleSpaces;
    }

    public async sendCorrespondence() {
        const updateStatusToPendingReview = this.canUpdateToPendingReview && this.selectedThread.subtaskStatusId == SubtaskStatusEnum.AwaitingResponse;
        const response = await this.messageCentreService.reply(this.selectedThread.guid, this.attachmentCorrespondenceId, this.anchorTagDecoration(this.message), true, false, false, updateStatusToPendingReview, null, this.addedPeople, null, null, null, this.task.emailIdentifier, this.task.emailIdentifierTypeName);

        if (response.validationResult?.hasError) {
            this.notificationClientService.show("Error", response.validationResult.errors[0].message, NotificationType.Error);
            return;
        }
        // Clear the attachment correspondence id after sending the message
        this.attachmentCorrespondenceId = null;
        // After sending new message, update the read indicator immediately
        await this.messageCentreService.updateCorrespondenceReadIndicator({
            subtaskId: this.selectedThread.subtaskId,
            email: this.task.emailIdentifier,
            correspondenceReadStatuses: [{ subtaskCorrespondenceId: response.subtaskCorrespondenceId, isRead: true }]
        });
        this.notificationClientService.show("Response was submitted.", "RightShip Operation will review your message and get back to you accordingly.", NotificationType.Success);
        this.message = "";
        this.expandedInput = false;
        
        // If attachment is closeout, automatically add to closeout documents
        if (this.selectedThread.isCloseout) {
            this.attachmentsModelToBeAdded.forEach(async (x) => {
                await this.messageCentreService.addAttachmentToEntityDocuments(x.path, x.name, this.task.taskId, this.task.entityType, this.selectedThread.closeoutAttachmentTypeId);
            })
        }
        this.attachmentsModelToBeAdded = [];
        const thread = this.threads.filter(x => x.subtaskId == this.selectedThread.subtaskId)[0];
        await this.selectThread(thread, true);
    }

    public getMessageDate(date: Date): string {
        if(moment(date).day() == moment().day()) {
            return "Today";
        }
        else {
            return moment(date).format('DD/MM/YYYY');
        }
    }

    public getMessageTime(date: Date): string {
        return moment(date).format('DD MMM YYYY HH:mm');
    }

    public formatDateTime(thread: any): string {
        return moment(thread.date).format('DD MMM YYYY HH:mm');
    }

    public openSendMessageDialog() {
        this.dialogClientService.show(MessageCentreDialogConstants.SendMessageDialog, {
            entityId: this.task.taskId,
            entityTypeId: this.task.entityType,
            emailIdentifier: this.task.emailIdentifier,
            checkBoxAction: this.task.newConversationCheckboxAction,
            onSave: async () => {   
                
            },
            afterSaved: async () => {
                this.dialogClientService.hide();
                await this.refreshThreads();
            },
            canEditRightshipRecipient: this.rightshipRecipientConfig.canEditRightshipRecipient,
            addedRsAsRecipient: this.rightshipRecipientConfig.addedRsAsRecipient,
            onRsRecipientToggle: this.rightshipRecipientConfig.onRsRecipientToggle.bind(this),
        }, true, false, false, "right");

        this.openedSendMessageDialog = true;
    }

    public openAttachmentDialog() {
        this.dialogClientService.show(MessageCentreDialogConstants.AddAttachmentToThisConversationDialog, {
            currentAttachments: this.attachmentsModelToBeAdded.map(x => {
                return {
                    name: x.name,
                    uri: x.uri,
                    entityAttachmentId: x.entityAttachmentId,
                    onDialogDeleted: () => {
                        this.attachmentsModelToBeAdded = this.attachmentsModelToBeAdded.filter(a => a.entityAttachmentId != x.entityAttachmentId)
                    }
                }
            }),
            entityType: EntityTypes.SubtaskCorrespondence,
            subtaskCorrespondenceId: this.attachmentCorrespondenceId,
            onSave: async () => {
                const updateStatusToPendingReview = this.canUpdateToPendingReview && this.selectedThread.subtaskStatusId == SubtaskStatusEnum.AwaitingResponse;
                const response = await this.messageCentreService.reply(this.selectedThread.guid, null, this.message, false, false, false, updateStatusToPendingReview, null, this.addedPeople, null, null, null, this.task?.emailIdentifier, this.task?.emailIdentifierTypeName);
                this.attachmentCorrespondenceId = response.subtaskCorrespondenceId;     
                return response.subtaskCorrespondenceId;
            },
            afterSaved: async (saveAttachmentResponse: any) => {
                this.attachmentsModelToBeAdded.push(...saveAttachmentResponse.map(x => {
                    return {
                        name: x.entityAttachmentsUpdated[0].name,
                        path: x.entityAttachmentsUpdated[0].path,
                        entityAttachmentId: x.entityAttachmentsUpdated[0].entityAttachmentId
                    }
                }));
            }
        }, true, false, false, "right");
    }

    public openAddPeopleDialog() {
        this.dialogClientService.show(MessageCentreDialogConstants.AddPeopleToThisconversationDialog, {
            entityId: this.task.taskId,
            entityTypeId: this.task.entityType,
            isRightshipAlerted : this.selectedThread.isRightShipAlerted,
            addPeopleToConversationFn: this.addPeopleToConversation.bind(this),
            peopleAddedToConversation: this.addedPeople
        },true);
    }

    public expandInput() {
        this.expandedInput = !this.expandedInput;
    }

    private addPeopleToConversation(emails: CorrespondenceEmailModel[]) {
        this.addedPeople = emails;
        this.addedPeopleVisible = true;
    }

    @computedFrom("addedPeople", "addedPeople.length")
    get peopleInConversation(): string {
        const to = this.addedPeople.filter(x => x.emailRecipientTypeId == 1).map(x => x.email).join(", ");
        const cc = this.addedPeople.filter(x => x.emailRecipientTypeId == 2).map(x => x.email).join(", ");
        const bcc = this.addedPeople.filter(x => x.emailRecipientTypeId == 3).map(x => x.email).join(", ");
        let html = "";
        if(to)
        {
            html += `<span><b>To:</b> ${to}</span>`;
        }
        if(cc)
        {
            html += `<span><b>Cc:</b> ${cc}</span>`;
        }
        if(bcc)
        {
            html += `<span><b>Bcc:</b> ${bcc}</span>`;
        }
        return html;
    }

    @computedFrom("attachmentsModelToBeAdded", "attachmentsModelToBeAdded.length")
    get attachmentsInConversation(): string {
        return this.attachmentsModelToBeAdded.map(x => x.name).join(", ");
    }

    public getThreadFromName(thread: VesselCorrespondence): string {
        if (thread.latestSenderEmail) {
            return thread.latestSenderEmail;
        }
        
        return "Rightship Operations";
    }

    public getWho(c: CorrespondenceClientModel) {
        if (c.messageSenderEmail) {
            return c.messageSenderEmail;
        }
        
        if (c.user) {
            return c.user;
        }

        if (c.emails.length > 0) {
            const from = c.emails.filter(x => x.isFrom)[0];
            if (from) return from.email;
            return c.emails[0].email;
        }
        return null;
    }

    public canNavigateBack():boolean {
		return this.router.history && this.router.history.getHistoryIndex() > 0;
	}

    public isMyMessage(message: CorrespondenceClientModel): boolean {
        return (message.messageSenderEmail && message.messageSenderEmail == this.task.emailIdentifier);
    }

    public overrideThreadReadIndicator(isUnread?: boolean) {
        const currentThread = this.filteredThreads.find(x => x.subtaskId == this.selectedThread.subtaskId);
        if (currentThread) {
            if (this.messages.filter(x => !this.isMyMessage(x)).every(x => x.isRead)) {
                currentThread.unreadMessageCount = 0;
            }
            if (isUnread) {
                // Assign whatever value here, it will automatically have the read indicator
                currentThread.unreadMessageCount = 1;
            }
        }
    }

    public async addAttachmentToDocuments(x: EntityAttachmentModel) {
        try {
            const attachmentTypeId = this.selectedThread.isCloseout ? this.selectedThread.closeoutAttachmentTypeId : null;
            await this.messageCentreService.addAttachmentToEntityDocuments(x.path, x.name, this.task.taskId, this.task.entityType, attachmentTypeId);
            this.notificationClientService.show("Add attachment", "Successfully added", NotificationType.Success);
        } catch {
            this.notificationClientService.show("Add attachment", "Error when adding document", NotificationType.Error);
        }
    }

    private findLastIndex<T>(array: Array<T>, predicate: (value: T, index: number, obj: T[]) => boolean): number {
        let l = array.length;
        while (l--) {
            if (predicate(array[l], l, array))
                return l;
        }
        return -1;
    }

    private anchorTagDecoration(text?: string) : string {
        return text ? text.replace(this.unlinkedUrlRegex, `<a href="$1" rel="noopener noreferrer" target="_blank">$1</a>`) : '';
    }
}
